import React, {useEffect, useState} from 'react';
import {Box, CircularProgress, Typography} from '@mui/material';
import {Attachment} from '../../../schemas/attachments/attachmentsSchema';
import '@react-pdf-viewer/core/lib/styles/index.css';
import {getAttachmentUrl} from '../../../utils/api/attachmentUrlBuilder';
import {cacheBlob, getCachedBlob} from '../../../utils/races/attachmentCache';
import {getFetcher} from '../../../utils/api/fetchers';
import PDFViewer from "./PDFViewer";

interface AttachmentViewerProps {
    raceId: number;
    attachment: Attachment;
}

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({raceId, attachment}) => {
    const [objectUrl, setObjectUrl] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        let url: string;

        const load = async () => {
            const key = `${raceId}-${attachment.id}-${attachment.name}`;
            const cached = await getCachedBlob(key);

            if (cached && isMounted) {
                url = URL.createObjectURL(cached);
                setObjectUrl(url);
                return;
            }

            const blob = await getFetcher<Blob>(getAttachmentUrl(raceId, attachment.id), {
                responseType: 'blob',
            });

            if (isMounted) {
                await cacheBlob(key, blob);
                url = URL.createObjectURL(blob);
                setObjectUrl(url);
            }
        };

        load();

        return () => {
            isMounted = false;
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [raceId, attachment]);

    if (!objectUrl) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <CircularProgress size={20}/>
                <Typography variant="body2">Načítání přílohy…</Typography>
            </Box>
        );
    }

    if (attachment.type.startsWith('image/')) {
        return (
            <Box sx={{height: '100%', width: '100%', maxHeight: '100%', maxWidth: '100%'}}>
                <img src={objectUrl} alt={attachment.name} style={{maxHeight: '100%', maxWidth: '100%'}}/>
            </Box>
        );
    } else if (attachment.type === 'application/pdf') {
        return (
            <Box sx={{height: '100%', width: '100%', maxHeight: '100%', maxWidth: '100%'}}>
                <PDFViewer objectUrl={objectUrl}/>
                <Typography variant="body2" sx={{textAlign: 'center'}}>
                    <a href={objectUrl} download={attachment.name}>Stáhnout PDF</a>
                </Typography>
            </Box>
        );
    } else {
        return (
            <Box>
                <Typography variant="body2">
                    Tento typ přílohy ({attachment.type}) nelze zobrazit přímo: {attachment.name}.{' '}
                    Otevřít v novém okně{' '}
                    <a href={objectUrl} target="_blank" rel="noreferrer">
                        {attachment.name}
                    </a>.
                </Typography>
            </Box>
        );
    }
};

export default AttachmentViewer;
